// Optimize an image stored in Cloudinary for optimal display on user's device and reduce bandwidth.

// **
// * TRANSFORMATIONS SUMMARY:
// * f_auto: Automatically selects the best image format for the user's browser
// * q_auto: Adjusts the image quality automatically to balance quality and file size
// * dpr_auto: Adjusts the image resolution to match the user's device pixel ratio for sharper images on high-res screens
// * w, h: Optional. Allows setting specific width and height to resize the image. Width defaults to w_auto if a specific value is not provided.
// * fl_progressive: Enables progressive loading for JPEG images, improving perceived loading speed
// * v: Optional. Uses versioning for cache busting when an image is updated
// **

const optimizeCloudinaryImg = (
  src: string,
  options: {
    width?: number;
    height?: number;
    version?: string | number;
    delay?: number;
  } = {}
) => {
  if (typeof src !== "string") return src;
  if (!src) return "";

  // Check if the image is a Cloudinary URL. If not, return the original source.
  if (!src.includes("res.cloudinary.com")) {
    return src;
  }

  // Remove any existing transformations within the /upload/ segment (ensures no duplicate params)
  const cleanSrc = src.replace(
    /\/upload\/(?:[^\/]*,)*q_[^,\/]*(?:,?[^\/]*)*\//,
    "/upload/"
  );

  // Default transformations
  const transformations = ["f_auto", "dpr_auto", "fl_progressive", "q_auto"];

  // Optional transfomrations
  if (options.width) {
    transformations.push(`w_${options.width}`);
  } else {
    transformations.push("w_auto");
  }
  if (options.height) transformations.push(`h_${options.height}`);

  // If the provided version does not start with "v", add it.
  let version = (options.version || "").toString();
  if (version && version[0] !== "v") {
    version = `v${version}`;
  }

  // Construct the URL with the new transformations and cleaned path
  const optimizedUrl = cleanSrc.replace(
    /\/upload\//,
    `/upload/${transformations.join(",")}${version ? `/${version}` : ""}/`
  );

  return optimizedUrl;
};

export default optimizeCloudinaryImg;
